import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ImageUpload, resolveSrc } from "../../components/ImageUpload";
import {
  eventsApi,
  pubsApi,
  boardApi,
  galleryApi,
  adminGetSubmissions,
  getContent,
  updateContent,
  formatApiErrorDetail,
} from "../../lib/api";
import { GROUPS, DEFAULTS } from "../../data/contentRegistry";

const CONFIGS = {
  gallery: {
    label: "Gallery",
    api: galleryApi,
    primary: "caption",
    image: "src",
    empty: { src: "", caption: "", order: 0 },
    fields: [
      { key: "src", label: "Image", type: "image" },
      { key: "caption", label: "Caption", type: "text" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  events: {
    label: "Events",
    api: eventsApi,
    primary: "title",
    empty: { category: "upcoming", title: "", date: "", location: "", description: "", order: 0 },
    fields: [
      { key: "category", label: "Category", type: "select", options: ["upcoming", "past"] },
      { key: "title", label: "Title", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  publications: {
    label: "Publications",
    api: pubsApi,
    primary: "title",
    empty: { category: "report", title: "", date: "", excerpt: "", author: "", slug: "", order: 0 },
    fields: [
      { key: "category", label: "Category", type: "select", options: ["report", "article", "research", "newsletter", "resource"] },
      { key: "title", label: "Title", type: "text" },
      { key: "date", label: "Date", type: "text" },
      { key: "excerpt", label: "Excerpt", type: "textarea" },
      { key: "author", label: "Author", type: "text" },
      { key: "slug", label: "Article slug (optional)", type: "text" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
  board: {
    label: "Board Members",
    api: boardApi,
    primary: "name",
    image: "image",
    empty: { name: "", role: "", image: "", bio: "", order: 0 },
    fields: [
      { key: "image", label: "Portrait", type: "image" },
      { key: "name", label: "Name", type: "text" },
      { key: "role", label: "Role / Title", type: "text" },
      { key: "bio", label: "Biography", type: "textarea" },
      { key: "order", label: "Order", type: "number" },
    ],
  },
};

const inputCls =
  "w-full bg-white border border-amf-navy/15 rounded-none px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy";

function EntityManager({ cfg }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {} or item
  const [form, setForm] = useState({});

  const load = () => cfg.api.list().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const openNew = () => {
    setForm({ ...cfg.empty });
    setEditing({});
  };
  const openEdit = (item) => {
    setForm({ ...item });
    setEditing(item);
  };
  const close = () => setEditing(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {};
    cfg.fields.forEach((f) => {
      payload[f.key] = f.type === "number" ? Number(form[f.key] || 0) : form[f.key] || "";
    });
    try {
      if (editing && editing.id) {
        await cfg.api.update(editing.id, payload);
        toast.success("Updated");
      } else {
        await cfg.api.create(payload);
        toast.success("Created");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await cfg.api.remove(item.id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`manager-${cfg.label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-2xl text-amf-navy">{cfg.label}</h2>
        <button
          onClick={openNew}
          data-testid="admin-add-button"
          className="amf-overline inline-flex items-center gap-2 bg-amf-burgundy text-amf-cream px-4 py-2.5 hover:bg-[#c24e12] transition-colors"
        >
          <Plus size={15} /> Add New
        </button>
      </div>

      <div className="space-y-px bg-amf-gold/25 border border-amf-gold/25">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid="admin-item-row"
            className="bg-white p-4 flex items-center gap-4"
          >
            {cfg.image && (
              <img
                src={resolveSrc(item[cfg.image])}
                alt=""
                className="w-12 h-12 object-cover border border-amf-gold/30 shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-playfair text-lg text-amf-navy truncate">{item[cfg.primary]}</p>
              {item.category && <span className="amf-overline text-amf-stone">{item.category}</span>}
            </div>
            <button onClick={() => openEdit(item)} data-testid="admin-edit-button" className="text-amf-navy hover:text-amf-burgundy p-2">
              <Pencil size={17} />
            </button>
            <button onClick={() => remove(item)} data-testid="admin-delete-button" className="text-amf-stone hover:text-amf-burgundy p-2">
              <Trash2 size={17} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <div className="bg-white p-8 text-center font-lora text-amf-stone">No items yet.</div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-amf-navy/60 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="bg-amf-cream w-full max-w-2xl p-8" data-testid="admin-form-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-playfair text-2xl text-amf-navy">
                {editing.id ? "Edit" : "New"} {cfg.label}
              </h3>
              <button onClick={close} className="text-amf-stone hover:text-amf-burgundy">
                <X size={22} />
              </button>
            </div>
            <form onSubmit={save} className="space-y-5" data-testid="admin-entity-form">
              {cfg.fields.map((f) => (
                <div key={f.key}>
                  <label className="amf-overline text-amf-stone block mb-2">{f.label}</label>
                  {f.type === "image" ? (
                    <ImageUpload value={form[f.key]} onChange={(v) => setField(f.key, v)} />
                  ) : f.type === "textarea" ? (
                    <textarea
                      rows={5}
                      value={form[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={`${inputCls} resize-none`}
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={form[f.key] || ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={inputCls}
                    >
                      {f.options.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : "text"}
                      value={form[f.key] ?? ""}
                      onChange={(e) => setField(f.key, e.target.value)}
                      className={inputCls}
                    />
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid="admin-save-button"
                  className="amf-overline bg-amf-burgundy text-amf-cream px-6 py-3 hover:bg-[#c24e12] transition-colors"
                >
                  Save
                </button>
                <button type="button" onClick={close} className="amf-overline border border-amf-navy text-amf-navy px-6 py-3 hover:bg-amf-navy hover:text-amf-cream transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Submissions() {
  const [subs, setSubs] = useState([]);
  useEffect(() => {
    adminGetSubmissions().then(setSubs).catch(() => setSubs([]));
  }, []);
  return (
    <div data-testid="manager-submissions">
      <h2 className="font-playfair text-2xl text-amf-navy mb-6">Submissions</h2>
      <div className="space-y-px bg-amf-gold/25 border border-amf-gold/25">
        {subs.map((s) => (
          <div key={s.id} className="bg-white p-5" data-testid="submission-row">
            <div className="flex flex-wrap items-center gap-3 mb-1">
              <span className="amf-overline text-amf-burgundy">{s.type}</span>
              <span className="font-playfair text-lg text-amf-navy">{s.name}</span>
              <a href={`mailto:${s.email}`} className="font-lora text-sm text-amf-stone hover:text-amf-burgundy">{s.email}</a>
            </div>
            {s.subject && <p className="font-inter text-sm text-amf-charcoal/70">Re: {s.subject}</p>}
            <p className="font-lora text-amf-charcoal/85 mt-2">{s.message}</p>
            <p className="amf-overline text-amf-stone mt-2 text-[0.65rem]">{new Date(s.created_at).toLocaleString()}</p>
          </div>
        ))}
        {subs.length === 0 && (
          <div className="bg-white p-8 text-center font-lora text-amf-stone">No submissions yet.</div>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: "content", label: "Page Content" },
  { key: "gallery", label: "Gallery" },
  { key: "events", label: "Events" },
  { key: "publications", label: "Publications" },
  { key: "board", label: "Board" },
  { key: "submissions", label: "Submissions" },
];

function ContentManager() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(GROUPS[0].group);

  useEffect(() => {
    getContent()
      .then((data) => {
        const merged = { ...DEFAULTS, ...data };
        setValues(merged);
      })
      .catch(() => setValues({ ...DEFAULTS }));
  }, []);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Send only non-default keys as updates; revert default-valued keys via deletes
      const changed = {};
      const deletes = [];
      Object.keys(values).forEach((k) => {
        if (values[k] !== DEFAULTS[k]) changed[k] = values[k];
        else deletes.push(k);
      });
      await updateContent(changed, deletes);
      toast.success("Page content saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const group = GROUPS.find((g) => g.group === active);

  return (
    <div data-testid="manager-content">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-playfair text-2xl text-amf-navy">Page Content</h2>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="amf-overline bg-amf-burgundy text-amf-cream px-5 py-2.5 hover:bg-[#c24e12] transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {GROUPS.map((g) => (
          <button
            key={g.group}
            onClick={() => setActive(g.group)}
            data-testid={`content-group-${g.group.toLowerCase().replace(/\s+/g, "-")}`}
            className={`amf-overline px-4 py-2 border transition-colors ${
              active === g.group
                ? "bg-amf-navy text-amf-cream border-amf-navy"
                : "border-amf-navy/20 text-amf-stone hover:border-amf-navy"
            }`}
          >
            {g.group}
          </button>
        ))}
      </div>

      <div className="space-y-6 max-w-3xl">
        {group.fields.map((f) => (
          <div key={f.key}>
            <label className="amf-overline text-amf-stone block mb-2">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea
                data-testid={`content-field-${f.key}`}
                rows={f.default.length > 160 ? 5 : 2}
                value={values[f.key] ?? ""}
                onChange={(e) => setVal(f.key, e.target.value)}
                className="w-full bg-white border border-amf-navy/15 rounded-none px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy resize-none"
              />
            ) : (
              <input
                data-testid={`content-field-${f.key}`}
                value={values[f.key] ?? ""}
                onChange={(e) => setVal(f.key, e.target.value)}
                className="w-full bg-white border border-amf-navy/15 rounded-none px-3 py-2 font-lora text-amf-charcoal focus:outline-none focus:border-amf-burgundy"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("gallery");

  return (
    <div className="min-h-screen bg-amf-cream" data-testid="admin-page">
      <header className="bg-amf-navy text-amf-cream">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/board/logo-circle.png" alt="AMF" className="h-9 w-9 rounded-full" />
            <span className="font-playfair text-lg">AMF Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="amf-overline text-amf-cream/70 hidden sm:inline">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="amf-overline inline-flex items-center gap-2 hover:text-amf-gold transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap gap-x-7 gap-y-2 border-b border-amf-gold/30 pb-4 mb-10">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`amf-overline pb-1 border-b-2 -mb-[18px] transition-colors ${
                tab === t.key
                  ? "text-amf-burgundy border-amf-burgundy"
                  : "text-amf-stone border-transparent hover:text-amf-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "submissions" ? (
          <Submissions />
        ) : tab === "content" ? (
          <ContentManager />
        ) : (
          <EntityManager cfg={CONFIGS[tab]} key={tab} />
        )}
      </div>
    </div>
  );
}
